import {
    useProject,
    useTask,
    useMeasurement,
    useInspectionAttributes,
    useDocument,
    useLead,
} from "../../../../../hooks/query";
import { useState } from "react";

export default function useCreateProjectHandler() {
    const { useCreateProject, useGetEagleViewReport } = useProject();
    const { useCreate: useCreateTask } = useTask();
    const { useCreate: useCreateMeasurement } = useMeasurement();
    const { useCreate: useCreateAttribute } = useInspectionAttributes();
    const { useInitialCreate: useCreateDocument } = useDocument();
    const { useCreateLead } = useLead();

    const createProjectSvc = useCreateProject();
    const readEagleViewReportSvc = useGetEagleViewReport();
    const createTaskSvc = useCreateTask();
    const createMeasurementSvc = useCreateMeasurement();
    const createAttributeSvc = useCreateAttribute();
    const createDocumentSvc = useCreateDocument();
    const createLeadSvc = useCreateLead();

    const [progress, setProgress] = useState({ loading: false, message: "", error: null, state: 0 });

    const readEagleViewReport = async (reportId: string) => {
        if (!reportId) {
            return;
        }
        const response = await readEagleViewReportSvc.mutateAsync({ ReportId: reportId });
        return response?.TotalMeasurements;
    };

    const createMeasurements = async (measurements: any, projectId: string) => {
        const params = {
            ProjectId: projectId,
            Area: parseFeet(measurements.AreaValue) ?? "0",
            Ridge: parseFeet(measurements.LengthRidge) ?? "0",
            Hip: parseFeet(measurements.LengthHip) ?? "0",
            Valley: parseFeet(measurements.LengthValley) ?? "0",
            Eave: parseFeet(measurements.LengthEave) ?? "0",
            Rake: parseFeet(measurements.LengthRake) ?? "0",
            "Step Flashing": "0",
            "Counter Flashing": "0",
            "Roof Vent": "0",
            "Pipe Jack": "0",
            "Furnace Vents": "0",
            "Bathroom Vents": "0",
            "Dryer Vents": "0",
            "Split Boots": "0",
            Headwall: "0",
            "Roll Metal": "0",
            "Soffit Length": "0",
            Chimney: "0",
            Skylights: "0",
            "Open Patio/Soffits": "0",
            "Satellite Dish": "0",
            "Solar Panels": "0",
        };

        await createMeasurementSvc.mutateAsync(params);
    };

    const createProject = async (data: any, hasEagleViewReport: boolean = false) => {
        // Note: EagleViewStatus and InsuranceStatus are calculated by the backend,
        // so we don't send them from the frontend

        // Use jobStartDate as StartDate if available, otherwise inspectionDate, otherwise current date
        // StartDate is required by the database, so we need a valid date
        const startDate = data.jobStartDate || data.inspectionDate || new Date().toISOString();

        const primarySalesman = data.salesmanName;
        const secondarySalesmanName = data.secondarySalesmanName;

        const params = {
            Name: data.name,
            Description: data.description,
            ProjectType: data.type,
            EagleViewOrderId: data.evorderid || "",
            // Store user IDs for tracking (new ID-based columns)
            PrimarySalesmanId: primarySalesman?.Id || primarySalesman?.value || "",
            SecondarySalesmanName: secondarySalesmanName || "",
            StartDate: startDate,
            EstimatedEndDate: data.estimatedEndDate || undefined,
            ActualEndDate: undefined, // ActualEndDate should be undefined initially, set when project is completed
            InspectionDate: data.inspectionDate || undefined,
            EstimateDate: data.estimateDate || undefined,
            PermitSubmissionDate: data.permitSubmissionDate || undefined,
            MaterialPurchaseDate: data.materialPurchaseDate || undefined,
            JobStartDate: data.jobStartDate || undefined,
            CompletionDate: undefined, // Not in form
            CustomerName: data.customerName,
            CustomerPhoneNumber: data.customerPhoneNumber,
            CustomerEmail: data.customerEmail || "",
            CustomerStreet: data.customerStreetAddress,
            CustomerCity: data.customerCity,
            CustomerState: data.customerState,
            CustomerZipCode: data.customerZipCode,
            CustomerCountry: data.customerCountry,
            PropertyStreet: data.propertyStreetAddress,
            PropertyCity: data.propertyCity,
            PropertyState: data.propertyState,
            PropertyZipCode: data.propertyZipCode,
            PropertyCountry: data.propertyCountry,
            InsuranceProvider: data.insuranceProvider || "",
            InsuranceClaimNumber: data.claimNumber || "",
            InsurancePolicyNumber: data.policyNumber || "",
            InsuranceAgentContact: "", // Not in form, but expected by backend
            InsuranceAgentNumber: data.adjusterContact || "",
            AdjusterContact: data.adjusterContact || "",
            AdjusterEmail: data.adjusterEmail || "",
            InsuranceAgentEmail: data.adjusterEmail || "",
            HasEagleViewReport: hasEagleViewReport,
            NoEagleViewId: data.noEagleViewId || false,
            NoInsurance: data.noInsurance || false,
        };
        // create project
        const response = await createProjectSvc.mutateAsync(params);
        return response;
    };

    const createAttributes = async (projectId: string) => {
        const params = {
            ProjectId: projectId,
        };
        await createAttributeSvc.mutateAsync(params);
    };

    const createDocuments = async (projectId: string) => {
        const params = {
            ProjectId: projectId,
        };
        await createDocumentSvc.mutateAsync(params);
    };

    const createTasks = async (projectId: string) => {
        for (const task of standardTasks) {
            const taskParams = {
                ProjectId: projectId,
                ParentTaskId: null,
                Name: task.name,
                Status: task.status,
                SortOrder: task.order,
            };
            await createTaskSvc.mutateAsync(taskParams);
        }
    };

    const createProjectEventHandler = async (data: any) => {
        try {
            let measurements = null;
            let hasEagleViewReport = false;
            const hasEagleViewId = data.evorderid && data.evorderid.trim() !== "" && !data.noEagleViewId;

            if (hasEagleViewId) {
                // Step 1: Load EagleView report
                setProgress({
                    loading: true,
                    message: progressStages[ProgressState.LoadingReport],
                    error: null,
                    state: ProgressState.LoadingReport,
                });

                try {
                    measurements = await readEagleViewReport(data.evorderid);
                    if (!measurements) {
                        throw new Error("The EagleView report was found, but it contains no measurement data. Please check the Order ID.");
                    }
                    hasEagleViewReport = true;

                    // Step 2: Report loaded
                    setProgress({
                        loading: true,
                        message: progressStages[ProgressState.ReportLoaded],
                        error: null,
                        state: ProgressState.ReportLoaded,
                    });
                } catch (error: any) {
                    // Re-throw to be caught by the main catch block and stop the process
                    const errorMsg = error.message || "Failed to retrieve the EagleView report.";
                    throw new Error(`EagleView Error: ${errorMsg} Project creation aborted.`);
                }
            } else {
                // Skip EagleView report loading
                setProgress({
                    loading: true,
                    message: "Preparing to create project with manual measurements...",
                    error: null,
                    state: ProgressState.ReportLoaded,
                });
            }

            // Step 3: Create project
            const projectDetails = await createProject(data, hasEagleViewReport);

            setProgress({
                loading: true,
                message: progressStages[ProgressState.ProjectCreated],
                error: null,
                state: ProgressState.ProjectCreated,
            });

            // Step 4: Create lead record and link to project
            const projectId = projectDetails.Id as string;
            const leadSource = data.leadSource;

            if (leadSource) {
                // ID will be generated in the backend
                await createLeadSvc.mutateAsync({
                    LeadSourcesId: leadSource.Id || leadSource.value,
                    Name: data.secondaryLead || null,
                    ProjectId: projectId,
                });
            }

            // Step 5: Create tasks
            await createTasks(projectId);

            setProgress({
                loading: true,
                message: progressStages[ProgressState.TasksCreated],
                error: null,
                state: ProgressState.TasksCreated,
            });

            // Step 6: Load measurements (from EagleView or manual entry)
            if (hasEagleViewId && measurements) {
                // Use EagleView measurements
                await createMeasurements(measurements, projectId);
            } else if (data.noEagleViewId) {
                // Use manual measurements from form
                await createMeasurements(
                    {
                        AreaValue: data.measurementArea,
                        LengthRidge: data.measurementRidge,
                        LengthHip: data.measurementHip,
                        LengthValley: data.measurementValley,
                        LengthEave: data.measurementEave,
                        LengthRake: data.measurementRake,
                    },
                    projectId
                );
            }

            setProgress({
                loading: true,
                message: progressStages[ProgressState.MeasurementsLoaded],
                error: null,
                state: ProgressState.MeasurementsLoaded,
            });

            await createAttributes(projectId);

            setProgress({
                loading: true,
                message: progressStages[ProgressState.AttributesCreated],
                error: null,
                state: ProgressState.AttributesCreated,
            });

            await createDocuments(projectId);

            setProgress({
                loading: false,
                message: progressStages[ProgressState.DocumentCreated],
                error: null,
                state: ProgressState.DocumentCreated,
            });

            return { projectId };
        } catch (e: any) {
            setProgress((prev) => ({
                ...prev,
                loading: false,
                error: e.message,
            }));
            console.error(e);
            return { projectId: null };
        }
    };

    function parseFeet(value: any): number {
        if (typeof value !== "string" && typeof value !== "number") return 0;
        const cleaned = String(value).replace(/[^\d.]/g, "");
        return parseFloat(cleaned) || 0;
    }

    return { createProjectEventHandler, progress };
}

const ProgressState = {
    LoadingReport: 0,
    ReportLoaded: 1,
    ProjectCreated: 2,
    TasksCreated: 3,
    MeasurementsLoaded: 4,
    AttributesCreated: 5,
    DocumentCreated: 6,
} as const;

const progressStages = [
    "Loading EagleView report... warming up the satellites.",
    "Report loaded successfully. Everything’s still in one piece.",
    "Project created — setting up tasks. Just a few moving parts.",
    "Tasks ready. Fetching measurements... accuracy is our middle name.",
    "Measurements are calculated. Creating attributes... naming things is harder than it looks.",
    "Documents templates are generating! This part takes a little brainpower.",
    "All set! Redirecting to your new project. Try not to get whiplash.",
];

const standardTasks = [
    {
        name: "Initial Inspection",
        status: "In Progress",
        order: 1,
    },
    {
        name: "Estimate/Contract",
        status: "To Do",
        order: 2,
    },
    {
        name: "Contract Signing",
        status: "To Do",
        order: 3,
    },
    {
        name: "Building Permit",
        status: "To Do",
        order: 4,
    },
    {
        name: "Material Ordering",
        status: "To Do",
        order: 5,
    },
    {
        name: "Job Execution",
        status: "To Do",
        order: 6,
    },
    {
        name: "Final Settlement",
        status: "To Do",
        order: 7,
    },
];
